function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _assertThisInitialized(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _inheritsLoose(subClass, superClass) { subClass.prototype = Object.create(superClass.prototype); subClass.prototype.constructor = subClass; subClass.__proto__ = superClass; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import gettext from 'js-utils/gettext';
import { debounce, find, isEmpty } from 'lodash';
import { HAS_WINDOW } from 'eventbrite_design_system/utils/dom';
import CrosshairSvg from 'eventbrite_design_system/iconography/icons/Crosshair';
import AutocompleteField from './AutocompleteFieldWithFallback';
import { getPlaceFromCoordinatesBase as getPlaceFromCoordinatesApi } from '../redux/location/api';
import { parseHistorySuggestions, getOnlineEventsOption } from '../utils/suggestions';
import { LOCATION_STORAGE_KEY } from '../constants';
import './LocationAutocomplete.scss';

var isGoogleMapsLoaded = function isGoogleMapsLoaded() {
  return HAS_WINDOW && window.google && window.google.maps;
};

var LOCATION_DEBOUNCE_INTERVAL = 250;
var BBOX_SEARCH_DISPLAY_STRING = gettext('map area'); // Takes a clicked-on location from the Autocomplete suggestions prop,
// gets the Place Details object from Google, then calls our own
// API for our internal Location properties

var getPlaceObj = function getPlaceObj(suggestedLocation, geocodeServiceClient) {
  var request = {
    // Google's place ID, not ours
    placeId: suggestedLocation.value
  };
  return new Promise(function (res, reject) {
    geocodeServiceClient.geocode(request, function (geocode, status) {
      if (status !== 'OK') {
        return reject("Received error from Google places: " + status);
      }

      var latitude = geocode[0].geometry.location.lat();
      var longitude = geocode[0].geometry.location.lng();
      var placeTypes = geocode[0].address_components[0].types;
      var placeType = 'locality';
      placeType = placeTypes.includes('administrative_area_level_1') ? 'region' : placeType;
      placeType = placeTypes.includes('country') ? 'country' : placeType; // Call our own API so that we can get a place ID and location slug
      // for the geocoordinates provided by Google

      return getPlaceFromCoordinatesApi({
        latitude: latitude,
        longitude: longitude,
        placeType: placeType
      }).then(function (response) {
        var place = response.place;

        if ('id' in place) {
          res({
            currentPlace: suggestedLocation.currentPlace,
            currentPlaceParent: suggestedLocation.currentPlaceParent,
            latitude: latitude,
            longitude: longitude,
            slug: place.location_slug,
            placeType: place.place_type,
            placeId: place.id,
            isOnline: false
          });
        } else {
          // note the difference between viewport and bounds.  bounds is the technical
          // boundary of a location while viewport is the commmonly accepted boundary
          // e.g. bounds for sf includes the farallon islands.  viewport doesn't.
          var viewport = geocode[0].geometry.viewport; // only 'sw' and 'ne' are necesary but at least one line of code uses 'se'
          // so we include 'nw' and 'se' just to be safe.

          var bbox = {
            nw: {
              lng: viewport.getSouthWest().lng(),
              lat: viewport.getNorthEast().lat()
            },
            sw: {
              lng: viewport.getSouthWest().lng(),
              lat: viewport.getSouthWest().lat()
            },
            ne: {
              lng: viewport.getNorthEast().lng(),
              lat: viewport.getNorthEast().lat()
            },
            se: {
              lng: viewport.getNorthEast().lng(),
              lat: viewport.getSouthWest().lat()
            }
          };
          res({
            currentPlace: suggestedLocation.currentPlace,
            currentPlaceParent: suggestedLocation.currentPlaceParent,
            latitude: latitude,
            longitude: longitude,
            isOnline: false,
            bbox: bbox
          });
        }
      });
    });
  });
};

var LocationAutocompleteGoogle =
/*#__PURE__*/
function (_PureComponent) {
  _inheritsLoose(LocationAutocompleteGoogle, _PureComponent);

  function LocationAutocompleteGoogle(props) {
    var _this;

    _this = _PureComponent.call(this, props) || this;

    _defineProperty(_assertThisInitialized(_this), "_handleLocationAutocompleteSelect", function (selectedValue) {
      var _this$props = _this.props,
          handleLocationSelect = _this$props.handleLocationSelect,
          handleUseCurrentLocation = _this$props.handleUseCurrentLocation,
          isUseCurrentLocationEnabled = _this$props.isUseCurrentLocationEnabled; // when an enter key is pressed, we want to make sure that any pending location queries
      // are completed.  i.e. If I type in "san" and the top suggestion is "san jose".  And
      // then I continue with "diego" and press enter quickly, the autocomplete component is
      // going to call this method before the suggestions for "san diego" have returned.

      if (_this._suggestionsPending) {
        return;
      }

      _this.setState({
        locationQuery: ''
      });

      var googleSuggestions = _this.state.googleSuggestions;
      var suggestedLocation = find(googleSuggestions, function (_ref) {
        var value = _ref.value;
        return value === selectedValue;
      });
      _this._isItemSelected = true;

      if (selectedValue.startsWith('recent-')) {
        // Selected a location from history
        var historySuggestion = _this.historySuggestions.find(function (_ref2) {
          var value = _ref2.value;
          return value === selectedValue;
        });

        handleLocationSelect(historySuggestion);
        _this._isItemSelected = false;
      } else if (suggestedLocation && suggestedLocation.value) {
        // Call Google for Place Details
        if (!isGoogleMapsLoaded()) {
          return;
        }

        var geocodeServiceClient = new window.google.maps.Geocoder();
        getPlaceObj(suggestedLocation, geocodeServiceClient).then(function (placeObj) {
          _this.setState({
            locationQuery: suggestedLocation.currentPlace,
            selectedLocation: suggestedLocation.currentPlace
          }, function () {
            handleLocationSelect(placeObj);
            _this._isItemSelected = false;
          });
        });
      } else if (selectedValue === 'online') {
        handleLocationSelect(_objectSpread({}, getOnlineEventsOption(), {
          placeId: null
        }));
        _this._isItemSelected = false;
      } else if (isUseCurrentLocationEnabled) {
        _this.setState({
          locationQuery: '',
          selectedLocation: ''
        }, function () {
          handleUseCurrentLocation();
          _this._isItemSelected = false;
        });
      }
    });

    _defineProperty(_assertThisInitialized(_this), "_handleLocationAutocompleteChange", function (locationQuery) {
      _this.setState({
        locationQuery: locationQuery
      });

      _this._suggestionsPending = true;

      _this._requestLocationSuggestions(locationQuery);
    });

    _defineProperty(_assertThisInitialized(_this), "_handleEnterKey", function (query) {
      if (_this._suggestionsPending) {
        _this._requestLocationSuggestions.cancel();

        _this._handleGetSuggestionsFromGoogle(query, true);
      }
    });

    _defineProperty(_assertThisInitialized(_this), "_handleLocationAutocompleteBlur", function () {
      var selectedLocation = _this.state.selectedLocation;

      _this.setState({
        locationQuery: selectedLocation,
        isTyping: false
      });
    });

    _defineProperty(_assertThisInitialized(_this), "_handleLocationAutocompleteFocus", function () {
      /* The autocomplete will refocus on the input, triggering the handlefocus
      callback when an item is selected. We don't want to clear the input _after_
      a user selected an item but only when initially clicking. At the moment,
      selecting an item triggers a redirect so it will be reset each time. */
      if (!_this._isItemSelected) {
        _this.historySuggestions = parseHistorySuggestions({
          storageKey: LOCATION_STORAGE_KEY
        });

        _this.setState({
          locationQuery: '',
          isTyping: true,
          googleSuggestions: []
        });

        _this._requestLocationSuggestions('');
      }
    });

    _defineProperty(_assertThisInitialized(_this), "_handleGetSuggestionsFromGoogle", function (query, selectFirstResult) {
      if (!query || !isGoogleMapsLoaded()) {
        return _this.setState({
          googleSuggestions: []
        });
      }

      var request = {
        input: query,
        types: ['(regions)']
      };
      var autocomplete = new window.google.maps.places.AutocompleteService();
      return autocomplete.getPlacePredictions(request, function (places, status) {
        _this._suggestionsPending = false; // In the case of ZERO_RESULTS, the suggestions will be empty,
        // exit early so that we will keep the most recent set of results

        if (status === 'ZERO_RESULTS' && !places) {
          return;
        }

        if (status !== 'OK') {
          throw new Error("Received error from Google places: " + status);
        }

        var placeSuggestions = places.map(function (place) {
          return {
            content: place.structured_formatting.main_text,
            secondaryContent: place.structured_formatting.secondary_text,
            value: place.place_id,
            currentPlace: place.structured_formatting.main_text,
            currentPlaceParent: place.structured_formatting.secondary_text
          };
        });

        _this.setState({
          googleSuggestions: placeSuggestions
        });

        if (selectFirstResult) {
          _this._handleLocationAutocompleteSelect(placeSuggestions[0].value);
        }
      });
    });

    _defineProperty(_assertThisInitialized(_this), "_requestLocationSuggestions", debounce(_this._handleGetSuggestionsFromGoogle, LOCATION_DEBOUNCE_INTERVAL));

    var currentPlace = props.currentPlace,
        isBBoxSearch = props.isBBoxSearch,
        suggestions = props.locationSuggestions.suggestions,
        googleMapsApiKey = props.googleMapsApiKey;
    var _locationQuery = currentPlace;

    if (isBBoxSearch) {
      _locationQuery = BBOX_SEARCH_DISPLAY_STRING;
    }

    _this.state = {
      locationQuery: _locationQuery,
      suggestions: suggestions,
      isTyping: false,
      selectedLocation: currentPlace,
      googleSuggestions: []
    };
    _this.historySuggestions = parseHistorySuggestions({
      storageKey: LOCATION_STORAGE_KEY
    });

    if (!isGoogleMapsLoaded() && googleMapsApiKey) {
      var loadjs = require('loadjs'); // Asynchronously load the Google Maps script, passing in the callback reference


      loadjs("https://maps.googleapis.com/maps/api/js?key=" + googleMapsApiKey + "&libraries=places", function () {
        if (_this.props.onGoogleMapsLoaded) {
          _this.props.onGoogleMapsLoaded();
        }
      });
    }

    return _this;
  }

  var _proto = LocationAutocompleteGoogle.prototype;

  _proto.componentDidMount = function componentDidMount() {
    var onComponentDidMount = this.props.onComponentDidMount;

    if (onComponentDidMount) {
      onComponentDidMount();
    }

    this._isItemSelected = false;
    this._suggestionsPending = false;
  };

  _proto.componentWillReceiveProps = function componentWillReceiveProps(_ref3) {
    var currentPlace = _ref3.currentPlace,
        isBBoxSearch = _ref3.isBBoxSearch;
    var newLocationQuery = currentPlace;

    if (isBBoxSearch) {
      newLocationQuery = BBOX_SEARCH_DISPLAY_STRING;
    }

    this.setState(function (_ref4) {
      var isTyping = _ref4.isTyping,
          currentLocationQuery = _ref4.locationQuery,
          currentSelectedLocation = _ref4.selectedLocation;
      var locationQuery = currentLocationQuery;
      var selectedLocation = currentSelectedLocation;

      if (!isTyping) {
        locationQuery = newLocationQuery;
        selectedLocation = newLocationQuery;
      }

      return {
        locationQuery: locationQuery,
        selectedLocation: selectedLocation
      };
    });
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    var onComponentWillUnmount = this.props.onComponentWillUnmount;

    if (onComponentWillUnmount) {
      onComponentWillUnmount();
    }
  };

  _proto.render = function render() {
    var _this$props2 = this.props,
        isWaitingForLocation = _this$props2.isWaitingForLocation,
        autocompleteProps = _this$props2.autocompleteProps,
        placeholder = _this$props2.placeholder,
        isUseCurrentLocationEnabled = _this$props2.isUseCurrentLocationEnabled;
    var _this$state = this.state,
        locationQuery = _this$state.locationQuery,
        selectedLocation = _this$state.selectedLocation,
        googleSuggestions = _this$state.googleSuggestions;
    var value = locationQuery;

    var _placeholder = placeholder || selectedLocation;

    var suggestions = [];

    if (isGoogleMapsLoaded()) {
      if (!isEmpty(googleSuggestions)) {
        suggestions = [].concat(googleSuggestions);
      } else {
        if (isUseCurrentLocationEnabled) {
          suggestions = [{
            id: 'use-current',
            content: gettext('Use my current location'),
            value: '',
            iconType: React.createElement(CrosshairSvg, null)
          }];
        }

        suggestions = [].concat(suggestions, [getOnlineEventsOption()], this.historySuggestions);
      }
    }

    if (isWaitingForLocation) {
      value = '';
      _placeholder = gettext('Finding your location...');
    }

    return React.createElement("div", {
      className: "location-autocomplete",
      "data-spec": "location-autocomplete"
    }, React.createElement(AutocompleteField, _extends({
      suggestions: suggestions,
      value: value,
      onBlur: this._handleLocationAutocompleteBlur,
      onChange: this._handleLocationAutocompleteChange,
      onFocus: this._handleLocationAutocompleteFocus,
      onSelect: this._handleLocationAutocompleteSelect,
      onEnterKey: this._handleEnterKey,
      shouldFocusInputOnSelect: false,
      isV2: true,
      placeholder: _placeholder
    }, autocompleteProps)));
  };

  return LocationAutocompleteGoogle;
}(PureComponent);

_defineProperty(LocationAutocompleteGoogle, "propTypes", {
  currentPlace: PropTypes.string,
  placeId: PropTypes.string,
  placeholder: PropTypes.string,
  onComponentDidMount: PropTypes.func,
  onComponentWillUnmount: PropTypes.func,
  onGoogleMapsLoaded: PropTypes.func,
  handleUseCurrentLocation: PropTypes.func,
  handleLocationSuggestionChange: PropTypes.func,
  handleLocationSelect: PropTypes.func,
  isBBoxSearch: PropTypes.bool,
  isUseCurrentLocationEnabled: PropTypes.bool,
  isOnlineEventsEnabled: PropTypes.bool,
  isOnline: PropTypes.bool,
  isWaitingForLocation: PropTypes.bool,
  isUsingCurrentLocation: PropTypes.bool,
  autocompleteProps: PropTypes.shape({
    borderType: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
    hideOutline: PropTypes.bool,
    isLimitedSearch: PropTypes.bool,
    preventInputValuePrefill: PropTypes.bool
  })
});

_defineProperty(LocationAutocompleteGoogle, "defaultProps", {
  autocompleteProps: {
    borderType: 'none',
    id: 'locationPicker',
    name: 'locationPicker',
    hideOutline: true,
    isLimitedSearch: true,
    preventInputValuePrefill: true
  }
});

export { LocationAutocompleteGoogle as default };